import {
  Box,
  Card,
  Chip,
  Grid,
  IconButton,
  Popover,
  Skeleton,
  Stack,
  Typography,
  Link,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import { useQuery } from "@tanstack/react-query";
import useApi from "../../hooks/useApi.js";
import { useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";

// Internal imports
import GenreDecadeLanguage from "../stats/GenreDecadeLanguage.js";
import ReleaseYear from "../stats/ReleaseYear.js";
import ListMenu from "../../components/ListMenu.js";
import Counts from "../stats/Counts.js";
import Ratings from "../stats/Ratings.js";
import FiveStarMovies from "../stats/FiveStarMovies.js";
import Directors from "../stats/Directors.js";
import { getContentLabel } from "../../utils/utils.js";

// Which finished movies the stats count: every one (null), or one year's
const TIME_DOMAIN_OPTIONS = [
  {
    label: "All-time",
    value: null,
  },
  {
    label: "2025",
    value: 2025,
  },
];

/**
 * The stats page's time span picker (all-time or a year) and an info button
 * explaining each, for its header.
 *
 * @param {number|null} timeDomain - The chosen year, or null for all-time
 * @param {(timeDomain: number|null) => void} onTimeDomainChange
 */
export function StatsTimeDomainControls({ timeDomain, onTimeDomainChange }) {
  const [timeDomainAnchorEl, setTimeDomainAnchorEl] = useState(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);

  const handleRangeClick = (event) => {
    setTimeDomainAnchorEl(event.currentTarget);
  };

  // Curried because ListMenu calls onSelect(option) to get each item's click handler
  const handleTimeDomainSelect = (option) => () => {
    onTimeDomainChange(option);
    setTimeDomainAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip
        variant="outlined"
        label={timeDomain === null ? "All-time" : timeDomain}
        size="small"
        clickable
        onClick={handleRangeClick}
        deleteIcon={<ExpandMoreIcon />}
        onDelete={handleRangeClick}
        sx={{ width: "fit-content" }}
      />
      <IconButton
        aria-label="about time spans"
        onClick={(event) => setInfoAnchorEl(event.currentTarget)}
        sx={{ p: 0 }}
      >
        <InfoIcon fontSize="small" />
      </IconButton>

      <ListMenu
        anchorEl={timeDomainAnchorEl}
        onClose={() => setTimeDomainAnchorEl(null)}
        onSelect={handleTimeDomainSelect}
        options={TIME_DOMAIN_OPTIONS}
        selectedValue={timeDomain}
      />

      {/* Anchored to the right, as the button now sits at the header's end */}
      <Popover
        id="stats-filter-popover"
        sx={{ mt: 1 }}
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setInfoAnchorEl(null)}
        disableRestoreFocus={process.env.NODE_ENV === "development"}
      >
        <Box sx={{ p: 2 }} maxWidth={300}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>All-time</strong>
            <br />
            Shows stats based on all movies that are currently marked as{" "}
            finished
          </Typography>

          <Typography variant="body2" gutterBottom>
            <strong>By year</strong>
            <br />
            Shows stats based on movies that have a finish date explicitly
            recorded within the selected year via a journal entry
          </Typography>
        </Box>
      </Popover>
    </Stack>
  );
}

/**
 * A user's stats for one content type (movies only so far), all-time or for a
 * chosen year (`timeDomain`, picked with StatsTimeDomainControls in the page
 * header). Below 10 finished movies it shows a "warming up" message instead
 * of the charts.
 */
export default function ProfileStats({
  username,
  contentType,
  isOwnProfile,
  timeDomain,
}) {
  const { fetchProfileStats } = useApi();
  const subject = isOwnProfile ? "You" : <em>@{username}</em>;

  const statsQuery = useQuery({
    queryKey: ["profileStats", username, contentType, timeDomain],
    queryFn: () => fetchProfileStats(contentType, username, timeDomain),
    enabled: !!username && contentType === "movie",
  });

  if (contentType === "tvshow" || contentType === "book") {
    return (
      <Card
        width="100%"
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) => theme.palette.secondary.main,
        }}
      >
        <Typography sx={{ fontStyle: "italic", py: 2 }}>
          Stats for{" "}
          {getContentLabel({
            type: contentType,
            plural: true,
            lowercase: true,
          })}{" "}
          coming soon!
        </Typography>
      </Card>
    );
  }

  return (
    <Grid container sx={{ py: 2, px: 0 }} spacing={2}>
      {statsQuery.isLoading && (
        <Grid
          item
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Skeleton
            variant="rectangular"
            height={150}
            sx={{ borderRadius: 1 }}
          />
        </Grid>
      )}

      {statsQuery.data && (
        <>
          <Counts
            data={statsQuery.data}
            timeDomain={timeDomain}
            subject={subject}
          />

          {statsQuery.data.counts.finished_count < 10 ? (
            <Grid item xs={12}>
              <Card
                width="100%"
                elevation={0}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: (theme) => theme.palette.secondary.main,
                  p: 2,
                }}
              >
                {isOwnProfile ? (
                  <>
                    <Typography variant="body2" gutterBottom>
                      Your stats are warming up! Finish at least 10 movies to
                      get started
                    </Typography>
                    <Typography variant="body2">
                      Need inspiration?{" "}
                      <Link component={ReactRouterLink} to="/explore">
                        Explore collections and start tracking
                      </Link>
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">
                    They're just getting started! No detailed stats to show just
                    yet...
                  </Typography>
                )}
              </Card>
            </Grid>
          ) : (
            <>
              <Ratings data={statsQuery.data} isOwnProfile={isOwnProfile} />
              <FiveStarMovies
                data={statsQuery.data}
                isOwnProfile={isOwnProfile}
                subject={subject}
              />
              <Directors
                data={statsQuery.data}
                isOwnProfile={isOwnProfile}
                subject={subject}
              />
              <ReleaseYear data={statsQuery.data} isOwnProfile={isOwnProfile} />
              <GenreDecadeLanguage
                data={statsQuery.data}
                isOwnProfile={isOwnProfile}
                subject={subject}
              />
            </>
          )}
        </>
      )}
    </Grid>
  );
}
